/**
 * useBidScenarios.js
 * React Query hooks for bid scenario CRUD.
 * Replaces the stub hooks previously in this file.
 *
 * Hooks exported:
 *   useListBidScenarios({ engagementType? })  → { data: { scenarios, total }, isLoading, … }
 *   useGetBidScenario(scenarioId)              → full scenario with services + rateOverrides
 *   useCreateBidScenario()                     → mutation
 *   useUpdateBidScenario()                     → mutation
 *   useDeleteBidScenario()                     → mutation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../../services/api';

const QUERY_KEY = 'bid-scenarios';

// ── API calls ─────────────────────────────────────────────────────────────────

const api = {
  list: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.engagementType) qs.set('engagement_type', params.engagementType);
    if (params.limit)          qs.set('limit', params.limit);
    return apiService.get(`/bid-scenarios/${qs.toString() ? `?${qs}` : ''}`);
  },

  get: (id) => apiService.get(`/bid-scenarios/${id}`),

  create: (body) => apiService.post('/bid-scenarios/', body),

  update: (id, body) => apiService.put(`/bid-scenarios/${id}`, body),

  delete: (id) => apiService.delete(`/bid-scenarios/${id}`),
};

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * List saved scenarios. Optionally filter by engagementType ('retainer'|'project').
 */
export function useListBidScenarios({ engagementType, limit = 100, enabled = true } = {}) {
  return useQuery({
    queryKey: [QUERY_KEY, 'list', engagementType],
    queryFn:  () => api.list({ engagementType, limit }),
    enabled,
    staleTime: 30_000,
    select: (data) => ({
      scenarios: data?.scenarios ?? [],
      total:     data?.total     ?? 0,
    }),
  });
}

/**
 * Get a single scenario by ID — includes full services[] and rateOverrides.
 * Only fetches when a non-null id is provided.
 */
export function useGetBidScenario(scenarioId) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', scenarioId],
    queryFn:  () => api.get(scenarioId),
    enabled:  !!scenarioId,
    staleTime: 60_000,
  });
}

/**
 * Create a new scenario.
 *
 * Payload shape (matches ScenarioCreate on the backend):
 * {
 *   name:             string,
 *   engagementType:   'retainer' | 'project',
 *   clientName:       string,
 *   services:         Service[],
 *   rateOverrides:    { [role]: number },   ← manual rate adjustments
 *   margin:           number,
 *   selectedTemplate: { id, engagement_name } | null,
 *   selectedBaseline: { id, label } | null,
 *   calculations:     { bidValue, minBid, maxBid, totalHours, totalCost },
 * }
 */
export function useCreateBidScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, 'list'] });
    },
  });
}

/**
 * Update an existing scenario.
 * Pass any subset of the create payload fields.
 */
export function useUpdateBidScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, ...payload }) => api.update(scenarioId, payload),
    onMutate: async ({ scenarioId, name }) => {
      await qc.cancelQueries({ queryKey: [QUERY_KEY, 'list'] });
      const prev = qc.getQueriesData({ queryKey: [QUERY_KEY, 'list'] });
      // Optimistically update name in list cache
      if (name) {
        qc.setQueriesData({ queryKey: [QUERY_KEY, 'list'] }, (old) => {
          if (!old?.scenarios) return old;
          return {
            ...old,
            scenarios: old.scenarios.map((s) =>
              s.id === scenarioId ? { ...s, name } : s
            ),
          };
        });
      }
      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        ctx.prev.forEach(([key, val]) => qc.setQueryData(key, val));
      }
    },
    onSuccess: (_, { scenarioId }) => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, 'list'] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY, 'detail', scenarioId] });
    },
  });
}

export function useDeleteBidScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scenarioId) => api.delete(scenarioId),
    onMutate: async (scenarioId) => {
      await qc.cancelQueries({ queryKey: [QUERY_KEY, 'list'] });
      const prev = qc.getQueriesData({ queryKey: [QUERY_KEY, 'list'] });
      // Optimistically remove from list cache
      qc.setQueriesData({ queryKey: [QUERY_KEY, 'list'] }, (old) => {
        if (!old?.scenarios) return old;
        return {
          ...old,
          scenarios: old.scenarios.filter((s) => s.id !== scenarioId),
          total: Math.max(0, (old.total ?? 1) - 1),
        };
      });
      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        ctx.prev.forEach(([key, val]) => qc.setQueryData(key, val));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, 'list'] });
    },
  });
}